import { getAccountTypeLabel } from '@/features/profile/accountTypeLabel';
import type { ClassCategory } from '@/features/profile/classes/classCategoryTypes';
import { buildProfileClassOrganizationLayout } from '@/features/profile/classes/classOrganizationUtils';
import type {
  OwnedClassCard,
  ProfilePostItem,
  UserAccountType,
  UserProfile,
} from '@/features/profile/types';
import type { ProfileClassOrganizationLayout } from '@/features/profile/classes/classCategoryTypes';
import { getInitials } from '@/lib/getInitials';

export const MOCK_OTHER_USER_PROFILE: UserProfile = {
  id: 'user-jordan',
  name: 'Jordan Lee',
  username: 'J_Lee',
  email: 'jordan.lee@myu.edu',
  accountType: 'student',
  accountTypeLabel: 'Student',
  institution: 'MyU English School',
  initials: 'JL',
  verified: false,
  isPrivate: false,
  stats: {
    posts: 4,
    following: 12,
    followers: 28,
    joinedClasses: 2,
  },
};

/** Private account demo — classes visible; posts and social lists hidden until followed. */
export const MOCK_PRIVATE_USER_PROFILE: UserProfile = {
  id: 'user-fatuma',
  name: 'Fatuma Hashi',
  username: 'ACAT_Fatuma',
  email: 'fatuma.hashi@myu.edu',
  accountType: 'management',
  accountTypeLabel: 'Staff',
  institution: 'ACA New Hires',
  initials: 'FH',
  verified: true,
  isPrivate: true,
  classesSectionTitle: 'ACA Salmiya Girls',
  stats: {
    posts: 4,
    following: 0,
    followers: 0,
    joinedClasses: 2,
  },
};

function stubFeedAuthorProfile(
  name: string,
  username: string,
  options?: {
    verified?: boolean;
    accountType?: UserAccountType;
  },
): UserProfile {
  return {
    id: `user-${username.toLowerCase()}`,
    name,
    username,
    email: `${username.toLowerCase()}@myu.edu`,
    accountType: options?.accountType ?? 'student',
    accountTypeLabel: getAccountTypeLabel(options?.accountType ?? 'student'),
    institution: 'MyU English School',
    initials: getInitials(name),
    verified: options?.verified ?? false,
    stats: {
      posts: 3,
      following: 8,
      followers: 12,
      joinedClasses: 2,
    },
  };
}

const FEED_AUTHOR_PROFILES: readonly UserProfile[] = [
  stubFeedAuthorProfile('Tr. Bader Yacoub', 'B_Yacoub', {
    verified: true,
    accountType: 'instructor',
  }),
  stubFeedAuthorProfile('Zamzam Hassan', 'Z_Hassan', { verified: true }),
  stubFeedAuthorProfile('Sarah Johnson', 'S_Johnson'),
  stubFeedAuthorProfile('Mike Chen', 'M_Chen'),
  stubFeedAuthorProfile('Jennifer Torres', 'J_Torres'),
  stubFeedAuthorProfile('Alex Martinez', 'A_Martinez'),
  stubFeedAuthorProfile('Emily Davis', 'E_Davis'),
  stubFeedAuthorProfile('Ryan Smith', 'R_Smith'),
];

export const MOCK_OTHER_USER_POSTS: readonly ProfilePostItem[] = [
  {
    id: 2001,
    isOwnPost: false,
    author: {
      name: 'Jordan Lee',
      avatar: '',
      initials: 'JL',
      verified: false,
      username: 'J_Lee',
    },
    timestamp: '12 Mar 2026',
    content: 'Looking forward to the group project discussion this week.',
    stats: { likes: 2, comments: 1, impressions: 15 },
  },
  {
    id: 2002,
    isOwnPost: false,
    author: {
      name: 'Jordan Lee',
      avatar: '',
      initials: 'JL',
      verified: false,
      username: 'J_Lee',
    },
    timestamp: '01 Mar 2026',
    content: 'Shared notes from today’s lecture are in the class folder.',
    stats: { likes: 5, comments: 0, impressions: 31 },
  },
] as const;

export const MOCK_OTHER_USER_CLASSES: readonly OwnedClassCard[] = [
  {
    id: 'class-jordan-phys',
    code: 'PHYS201',
    name: 'Physics 201',
    postCount: 6,
    memberCount: 52,
    isPrivate: false,
    dotClassName: 'bg-[#60a5fa]',
  },
] as const;

const MOCK_OTHER_USER_CATEGORIES: readonly ClassCategory[] = [
  {
    id: 'cat-jordan-sciences',
    name: 'Sciences',
    classIds: ['class-jordan-phys'],
    sortOrder: 0,
  },
] as const;

const MOCK_OTHER_USER_UNCATEGORIZED: readonly string[] = [];

const MOCK_PRIVATE_USER_CATEGORIES: readonly ClassCategory[] = [
  {
    id: 'cat-fatuma-sg',
    name: 'SG Channels',
    classIds: ['class-sg-announcements', 'class-sg-policies'],
    sortOrder: 0,
  },
] as const;

const MOCK_PRIVATE_USER_UNCATEGORIZED: readonly string[] = [];

export const MOCK_PRIVATE_USER_CLASSES: readonly OwnedClassCard[] = [
  {
    id: 'class-sg-announcements',
    code: 'SG-ANN',
    name: 'SG - General Announcements',
    postCount: 46,
    memberCount: 52,
    isPrivate: true,
    dotClassName: 'bg-[#fb923c]',
  },
  {
    id: 'class-sg-policies',
    code: 'SG-POL',
    name: 'SG - Policies and Resources',
    postCount: 13,
    memberCount: 52,
    isPrivate: true,
    dotClassName: 'bg-[#a78bfa]',
  },
] as const;

const PROFILE_LOOKUP: Record<string, UserProfile> = Object.fromEntries(
  [MOCK_OTHER_USER_PROFILE, MOCK_PRIVATE_USER_PROFILE, ...FEED_AUTHOR_PROFILES].map((profile) => [
    profile.username.toLowerCase(),
    profile,
  ]),
);

export function getAllSearchableProfiles(excludeUsername?: string): UserProfile[] {
  const exclude = excludeUsername?.toLowerCase();
  return Object.values(PROFILE_LOOKUP).filter(
    (profile) => profile.username.toLowerCase() !== exclude,
  );
}

export function resolveProfileByUsername(username: string): UserProfile | undefined {
  return PROFILE_LOOKUP[username.toLowerCase()];
}

export function resolveProfilePosts(username: string): readonly ProfilePostItem[] {
  if (username.toLowerCase() === MOCK_OTHER_USER_PROFILE.username.toLowerCase()) {
    return MOCK_OTHER_USER_POSTS;
  }
  return [];
}

export function resolveProfileClasses(username: string): readonly OwnedClassCard[] {
  const layout = resolveProfileClassOrganization(username);
  if (layout) {
    return [...layout.uncategorized, ...layout.categories.flatMap((group) => group.classes)];
  }
  return [];
}

export function resolveProfileClassOrganization(
  username: string,
): ProfileClassOrganizationLayout | undefined {
  const key = username.toLowerCase();
  if (key === MOCK_OTHER_USER_PROFILE.username.toLowerCase()) {
    return buildProfileClassOrganizationLayout(
      MOCK_OTHER_USER_CLASSES,
      MOCK_OTHER_USER_CATEGORIES,
      MOCK_OTHER_USER_UNCATEGORIZED,
    );
  }
  if (key === MOCK_PRIVATE_USER_PROFILE.username.toLowerCase()) {
    return buildProfileClassOrganizationLayout(
      MOCK_PRIVATE_USER_CLASSES,
      MOCK_PRIVATE_USER_CATEGORIES,
      MOCK_PRIVATE_USER_UNCATEGORIZED,
    );
  }
  return undefined;
}
